import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  MessageSquareHeart, Star, CheckCircle2, XCircle, EyeOff, Pencil, MapPin, Users, Shield, Loader2,
} from "lucide-react";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "private", label: "Private" },
  { key: "rejected", label: "Rejected" },
];
const STATUS_STYLE = {
  pending: "bg-[#FEF3C7] text-[#92400E]", approved: "bg-[#D1FAE5] text-[#065F46]",
  rejected: "bg-[#FEE2E2] text-[#991B1B]", private: "bg-[#E5E7EB] text-[#374151]",
};
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString([], { dateStyle: "medium" }); } catch { return iso; } };

export default function Testimonials() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [edit, setEdit] = useState(null);
  const [editText, setEditText] = useState("");
  const [reject, setReject] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const load = () => api.get(`/admin/testimonials?status=${tab}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    setData(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const act = async (id, path, body) => {
    setBusy(id);
    try { await api.post(`/admin/testimonials/${id}/${path}`, body || {}); toast.success("Updated."); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const saveEdit = async () => {
    if (!editText.trim()) { toast.error("Text cannot be empty."); return; }
    setBusy(edit.id);
    try { await api.put(`/admin/testimonials/${edit.id}`, { text: editText.trim() }); toast.success("Saved."); setEdit(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const doReject = async () => { await act(reject.id, "reject", { note: rejectNote.trim() || null }); setReject(null); setRejectNote(""); };

  const setDisplay = async (id, body) => {
    setBusy(id);
    try { await api.put(`/admin/testimonials/${id}/display`, body); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const counts = data?.counts || {};

  return (
    <div className="max-w-4xl" data-testid="admin-testimonials-page">
      <h1 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2 mb-1"><MessageSquareHeart size={22} /> Family feedback & testimonials</h1>
      <p className="text-[15px] text-muted-foreground mb-5">Review what families share. Nothing is public until you approve it.</p>

      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`testimonials-tab-${t.key}`}
            className={`h-9 rounded-full px-4 text-[14px] font-medium transition-colors ${tab === t.key ? "bg-[#1C4358] text-white" : "bg-[#EEF2F5] text-[#4B5563] hover:bg-[#E1E8ED]"}`}>
            {t.label}{counts[t.key] ? ` (${counts[t.key]})` : ""}
          </button>
        ))}
      </div>

      {!data && <div className="space-y-3"><Skeleton className="h-32" /><Skeleton className="h-32" /></div>}
      {data && data.items.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground border-[#E6EDF2]" data-testid="testimonials-empty">No {tab} feedback right now.</Card>
      )}

      <div className="space-y-3">
        {data?.items.map((t) => (
          <Card key={t.id} className="p-5 border-[#E6EDF2]" data-testid={`testimonial-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#252221]">{t.submitted_by_name}</span>
                {t.is_family && <Badge variant="outline" className="gap-1 text-[11px]"><Users size={11} /> Family member</Badge>}
                <Badge className={`text-[11px] capitalize ${STATUS_STYLE[t.status]}`}>{t.status}</Badge>
                {t.permission_public
                  ? <Badge className="bg-[#DBEAFE] text-[#1E40AF] text-[11px] gap-1"><Shield size={11} /> OK to publish</Badge>
                  : <Badge variant="outline" className="text-[11px] gap-1"><EyeOff size={11} /> Private consent</Badge>}
              </div>
              {t.rating ? (
                <div className="flex items-center gap-0.5 shrink-0" data-testid={`testimonial-rating-${t.id}`}>
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} className={n <= t.rating ? "fill-[#E0A526] text-[#E0A526]" : "text-[#D6E0E7]"} />)}
                </div>
              ) : null}
            </div>

            <p className="text-[15px] text-[#252221] mt-3 whitespace-pre-wrap">&ldquo;{t.text}&rdquo;</p>

            <div className="text-[12px] text-[#6B7280] mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>Display: <span className="font-medium capitalize">{(t.name_display || "").replace("_", " ")}</span> → &ldquo;{t.display_name}&rdquo;</span>
              {(t.city || t.state) && <span className="flex items-center gap-1"><MapPin size={11} /> {[t.city, t.state].filter(Boolean).join(", ")}</span>}
              {t.service_lane && <span className="capitalize">{String(t.service_lane).replace(/[-_]/g, " ")}</span>}
              <span>Submitted {fmt(t.submitted_at)}</span>
              {t.reviewed_by_name && <span>· Reviewed by {t.reviewed_by_name} {t.reviewed_at ? fmt(t.reviewed_at) : ""}</span>}
            </div>
            {t.admin_note && <div className="text-[12px] text-[#991B1B] mt-1">Note: {t.admin_note}</div>}

            <div className="flex flex-wrap gap-2 mt-4">
              {t.status !== "approved" && (
                <Button size="sm" disabled={busy === t.id} onClick={() => act(t.id, "approve")} data-testid={`testimonial-approve-${t.id}`}
                  className="bg-[#2F7D5A] hover:bg-[#276849] text-white gap-1.5 h-9 rounded-full">
                  {busy === t.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Approve for public use
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => { setEdit(t); setEditText(t.text); }} data-testid={`testimonial-edit-${t.id}`} className="gap-1.5 h-9 rounded-full">
                <Pencil size={14} /> Edit copy
              </Button>
              {t.status !== "private" && (
                <Button size="sm" variant="outline" onClick={() => act(t.id, "private")} data-testid={`testimonial-private-${t.id}`} className="gap-1.5 h-9 rounded-full">
                  <EyeOff size={14} /> Keep private
                </Button>
              )}
              {t.status !== "rejected" && (
                <Button size="sm" variant="ghost" onClick={() => setReject(t)} data-testid={`testimonial-reject-${t.id}`} className="gap-1.5 h-9 rounded-full text-[#9C1E22] hover:bg-[#FEE2E2]">
                  <XCircle size={14} /> Reject
                </Button>
              )}
            </div>

            {t.status === "approved" && t.permission_public && (
              <div className="mt-3 pt-3 border-t border-[#EEF2F5] flex flex-wrap items-center gap-x-6 gap-y-2" data-testid={`testimonial-display-${t.id}`}>
                <label className="flex items-center gap-2 text-[13px] text-[#252221] cursor-pointer">
                  <Switch checked={!!t.show_on_homepage} disabled={busy === t.id}
                    onCheckedChange={(v) => setDisplay(t.id, { show_on_homepage: v })} data-testid={`testimonial-homepage-${t.id}`} />
                  Show on homepage
                </label>
                <label className="flex items-center gap-2 text-[13px] text-[#252221] cursor-pointer">
                  <Switch checked={!!t.featured} disabled={busy === t.id}
                    onCheckedChange={(v) => setDisplay(t.id, { featured: v })} data-testid={`testimonial-featured-${t.id}`} />
                  Featured
                </label>
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="testimonial-edit-dialog">
          <DialogHeader><DialogTitle>Light copyedit</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Edit the testimonial text for spelling and formatting only.</DialogDescription>
          <p className="text-[13px] text-muted-foreground -mt-1">Fix spelling or formatting only — keep the family&apos;s voice.</p>
          <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={5} className="text-[15px]" data-testid="testimonial-edit-text" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEdit(null)} className="rounded-full">Cancel</Button>
            <Button onClick={saveEdit} className="bg-[#1C4358] text-white rounded-full" data-testid="testimonial-edit-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent data-testid="testimonial-reject-dialog">
          <DialogHeader><DialogTitle>Reject feedback</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Reject this feedback with an optional internal note.</DialogDescription>
          <Textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} rows={3} placeholder="Optional internal note…" className="text-[15px]" data-testid="testimonial-reject-note" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setReject(null)} className="rounded-full">Cancel</Button>
            <Button onClick={doReject} className="bg-[#9C1E22] text-white rounded-full" data-testid="testimonial-reject-confirm">Reject</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
